/* ==========================================================================
   DUPE OUTDOORS — FIELD INSTRUMENT
   Motion, scroll chrome, and the newsletter capture.

   Design contract:
   - Everything degrades to a working, readable page if JS never runs.
     Reveal targets are visible by default; JS ADDS the hidden state only
     when it is able to observe them. Never ship a page that needs JS to
     show its content.
   - prefers-reduced-motion disables transforms, parallax and count-ups.
     The popup still works, because it is function, not decoration.
   - Popup state lives in localStorage and is wrapped in try/catch. Private
     mode and blocked-storage browsers must not throw.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STORE_KEY = 'dupe_nl_state';  // 'subscribed' | 'dismissed' | null

  /* ---------- storage helpers (never throw) ---------- */
  function readState() {
    try { return window.localStorage.getItem(STORE_KEY); } catch (e) { return null; }
  }
  function writeState(v) {
    try { window.localStorage.setItem(STORE_KEY, v); } catch (e) { /* no-op */ }
  }

  /* ======================================================================
     1. SCROLL REVEAL
     Elements carrying [data-reveal] fade + rise as they enter view.
     Children with [data-reveal-child] stagger inside their parent.
     ====================================================================== */
  function initReveal() {
    var targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      // Leave everything visible. Nothing to do.
      return;
    }

    // Only now do we hide them — so a JS failure above leaves content shown.
    for (var i = 0; i < targets.length; i++) targets[i].classList.add('is-armed');

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('is-revealed');

        var kids = el.querySelectorAll('[data-reveal-child]');
        for (var k = 0; k < kids.length; k++) {
          // Cap the stagger so a long grid does not take 3 seconds to appear.
          kids[k].style.transitionDelay = Math.min(k * 60, 480) + 'ms';
        }
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    for (var j = 0; j < targets.length; j++) io.observe(targets[j]);
  }

  /* ======================================================================
     2. SCROLL PROGRESS — the altimeter rail at the top of the viewport
     ====================================================================== */
  function initProgress() {
    var bar = document.querySelector('[data-scroll-progress]');
    if (!bar || REDUCED) return;

    var ticking = false;
    function update() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      var pct = max > 0 ? (window.scrollY / max) : 0;
      bar.style.transform = 'scaleX(' + Math.max(0, Math.min(1, pct)) + ')';
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /* ======================================================================
     3. HERO PARALLAX
     Transform only — never top/height — so it stays on the compositor.
     ====================================================================== */
  function initParallax() {
    var layers = document.querySelectorAll('[data-parallax]');
    if (!layers.length || REDUCED) return;

    var ticking = false;
    function update() {
      var y = window.scrollY;
      for (var i = 0; i < layers.length; i++) {
        var rate = parseFloat(layers[i].getAttribute('data-parallax')) || 0.15;
        // Stop translating once the hero is well off screen.
        if (y < window.innerHeight * 1.4) {
          layers[i].style.transform = 'translate3d(0,' + (y * rate) + 'px,0)';
        }
      }
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    update();
  }

  /* ======================================================================
     4. COUNT-UP NUMERICS
     [data-countup="1174"] ticks from 0 when it enters view.
     ====================================================================== */
  function initCountUp() {
    var nums = document.querySelectorAll('[data-countup]');
    if (!nums.length || !('IntersectionObserver' in window)) return;

    if (REDUCED) {
      for (var r = 0; r < nums.length; r++) {
        nums[r].textContent = formatNum(parseFloat(nums[r].getAttribute('data-countup')));
      }
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        run(entry.target);
        io.unobserve(entry.target);
      });
    }, { threshold: 0.5 });

    for (var i = 0; i < nums.length; i++) io.observe(nums[i]);

    function run(el) {
      var target = parseFloat(el.getAttribute('data-countup')) || 0;
      var prefix = el.getAttribute('data-prefix') || '';
      var suffix = el.getAttribute('data-suffix') || '';
      var dur = 1100;
      var t0 = null;
      function step(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min((ts - t0) / dur, 1);
        // easeOutExpo — fast start, soft landing. Reads as instrumentation.
        var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
        el.textContent = prefix + formatNum(target * eased) + suffix;
        if (p < 1) window.requestAnimationFrame(step);
      }
      window.requestAnimationFrame(step);
    }

    function formatNum(n) {
      return (Math.round(n * 100) / 100 % 1 === 0)
        ? Math.round(n).toLocaleString('en-US')
        : n.toFixed(2);
    }
  }
  function formatNum(n) {
    return (Math.round(n * 100) / 100 % 1 === 0)
      ? Math.round(n).toLocaleString('en-US')
      : n.toFixed(2);
  }

  /* ======================================================================
     5. NEWSLETTER CAPTURE
     Auto-opens once per visitor. Dismissing it reveals a persistent side
     tab so an accidental X never costs the signup.
     ====================================================================== */
  function initNewsletter() {
    var modal = document.querySelector('[data-nl-modal]');
    var tab   = document.querySelector('[data-nl-tab]');
    if (!modal) return;

    var panel     = modal.querySelector('[data-nl-panel]');
    var form      = modal.querySelector('form');
    var successEl = modal.querySelector('[data-nl-success]');
    var errorEl   = modal.querySelector('[data-nl-error]');
    var closers   = modal.querySelectorAll('[data-nl-close]');
    var emailIn   = modal.querySelector('input[type="email"]');

    var state = readState();
    var opened = false;
    var lastFocus = null;

    /* --- open / close --- */
    function open(source) {
      if (modal.classList.contains('is-open')) return;
      lastFocus = document.activeElement;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('dupe-noscroll');
      opened = true;
      // Focus the field, but not on first paint — let the entrance play.
      window.setTimeout(function () {
        if (emailIn && source !== 'auto') emailIn.focus();
      }, 420);
    }

    function close(remember) {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('dupe-noscroll');
      if (remember && readState() !== 'subscribed') {
        writeState('dismissed');
        showTab();
      }
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function showTab() { if (tab) tab.classList.add('is-visible'); }
    function hideTab() { if (tab) tab.classList.remove('is-visible'); }

    /* --- triggers --- */
    if (state === 'subscribed') {
      hideTab();                       // done — never bother them again
    } else if (state === 'dismissed') {
      showTab();                       // they closed it; keep the door open
    } else if (/^\/(products|cart|checkout)/.test(window.location.pathname)) {
      // 2026-10-14: never cover a product or the cart with a popup. The tab is enough there.
      showTab();
    } else {
      // First visit: 30 seconds, or once they have scrolled most of the page (was 8 s / 400 px,
      // which threw a full-screen modal at people still looking at the first photo).
      var timer = window.setTimeout(function () { open('auto'); }, 30000);
      var onScroll = function () {
        var deep = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
        if (deep > 0.7 && !opened) {
          window.clearTimeout(timer);
          open('auto');
          window.removeEventListener('scroll', onScroll);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    /* --- wiring --- */
    for (var c = 0; c < closers.length; c++) {
      closers[c].addEventListener('click', function (e) { e.preventDefault(); close(true); });
    }
    modal.addEventListener('click', function (e) {
      if (e.target === modal) close(true);          // backdrop click
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) close(true);
    });
    if (tab) {
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        open('tab');
      });

      /* Tuck the tab while the offer band is on screen. The band makes the
         same offer with its own red 10% bubble, and on a phone the floating
         pill landed right on top of it - two 10% badges colliding. */
      var offers = document.querySelectorAll('.f-offer');
      if (offers.length && 'IntersectionObserver' in window) {
        var visible = [];
        var io = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            var i = visible.indexOf(en.target);
            if (en.isIntersecting && i < 0) visible.push(en.target);
            if (!en.isIntersecting && i >= 0) visible.splice(i, 1);
          });
          tab.classList.toggle('is-tucked', visible.length > 0);
        }, { threshold: 0.15 });
        for (var o = 0; o < offers.length; o++) io.observe(offers[o]);
      }
    }

    /* Any [data-nl-open] on the page opens the modal — the offer band's
       button uses this. If they already subscribed, skip the form and show
       them the code again rather than asking for an email twice. */
    var openers = document.querySelectorAll('[data-nl-open]');
    for (var o = 0; o < openers.length; o++) {
      openers[o].addEventListener('click', function (e) {
        e.preventDefault();
        if (readState() === 'subscribed') succeed();
        open('button');
      });
    }

    /* --- keep focus inside the panel while it is open --- */
    modal.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !modal.classList.contains('is-open')) return;
      var f = panel.querySelectorAll('a[href], button, input, [tabindex]:not([tabindex="-1"])');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    /* --- submit: POST in the background so we can reveal the code
           without a page reload. Falls back to a native submit. --- */
    if (form) {
      form.addEventListener('submit', function (e) {
        if (!window.fetch || !window.FormData) return;   // let it submit natively
        e.preventDefault();

        var btn = form.querySelector('[type="submit"]');
        if (btn) { btn.disabled = true; btn.setAttribute('data-busy', 'true'); }
        if (errorEl) errorEl.hidden = true;

        fetch(form.action, {
          method: 'POST',
          body: new FormData(form),
          headers: { 'Accept': 'text/html' }
        })
          .then(function (res) {
            // Shopify redirects to ?customer_posted=true on success.
            if (res.ok && res.url && res.url.indexOf('customer_posted=true') !== -1) return succeed();
            if (res.ok) return succeed();   // 200 with no redirect: treat as accepted
            throw new Error('rejected');
          })
          .catch(function () {
            if (btn) { btn.disabled = false; btn.removeAttribute('data-busy'); }
            if (errorEl) {
              errorEl.hidden = false;
            } else {
              form.submit();                // last resort: hand it to the browser
            }
          });
      });
    }

    function succeed() {
      writeState('subscribed');
      hideTab();
      if (form) form.hidden = true;
      if (successEl) successEl.hidden = false;
      var codeEl = modal.querySelector('[data-nl-code]');
      if (codeEl) {
        codeEl.classList.add('is-lit');
        var copyBtn = modal.querySelector('[data-nl-copy]');
        if (copyBtn && navigator.clipboard) {
          copyBtn.addEventListener('click', function () {
            navigator.clipboard.writeText(codeEl.getAttribute('data-code') || '')
              .then(function () {
                copyBtn.textContent = 'Copied';
                window.setTimeout(function () { copyBtn.textContent = 'Copy code'; }, 2000);
              })
              .catch(function () { /* clipboard blocked — the code is on screen anyway */ });
          });
        }
      }
    }
  }

  /* ======================================================================
     6. COLOUR CYCLE — card photos that crossfade through every colour
     Markup: snippets/colour-cycle.liquid. [data-colour-cycle] holds the
     card's own photo (colour 1) with [data-cc-frame] images for the other
     colours stacked on top, in colour order.
     Showing a frame fades it in OVER the one before, and the old frame is
     only dropped once it is covered, so a change never flashes back through
     to colour 1. Returning to colour 1 just fades the top frame out.
     One shared clock so every visible card changes on the same beat; it
     only runs while a card is on screen. Reduced motion stays on colour 1.
     ====================================================================== */
  var CC_HOLD = 2600;  // ms each colour stays up
  var CC_FADE = 700;   // matches the .cc__frame opacity transition

  function initColourCycle() {
    var roots = document.querySelectorAll('[data-colour-cycle]');
    if (!roots.length || REDUCED || !('IntersectionObserver' in window)) return;

    var cards = [];
    var visible = 0;
    var clock = null;

    function step(card) {
      var n = card.frames.length + 1;
      var next = (card.at + 1) % n;
      if (next > 0) {
        var img = card.frames[next - 1];
        // Not downloaded yet: hold this colour and try again next beat,
        // rather than fading to an empty frame.
        if (!img.complete || !img.naturalWidth) return;
        img.classList.add('is-on');
      }
      if (card.at > 0) {
        var old = card.frames[card.at - 1];
        if (next === 0) old.classList.remove('is-on');
        else window.setTimeout(function () { old.classList.remove('is-on'); }, CC_FADE);
      }
      Array.prototype.forEach.call(card.dots, function (d, i) {
        d.classList.toggle('is-on', i === next);
      });
      card.at = next;
    }

    function tick() {
      cards.forEach(function (c) { if (c.onScreen) step(c); });
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var card = entry.target._cc;
        if (!card || card.onScreen === entry.isIntersecting) return;
        card.onScreen = entry.isIntersecting;
        visible += card.onScreen ? 1 : -1;
        if (card.onScreen) {
          // Fetch the other colours now, while there is a beat to spare.
          Array.prototype.forEach.call(card.frames, function (f) { f.loading = 'eager'; });
        }
      });
      if (visible > 0 && !clock) clock = window.setInterval(tick, CC_HOLD);
      if (visible === 0 && clock) { window.clearInterval(clock); clock = null; }
    }, { threshold: 0.35 });

    Array.prototype.forEach.call(roots, function (root) {
      var frames = root.querySelectorAll('[data-cc-frame]');
      if (!frames.length) return;
      var card = { frames: frames, dots: root.querySelectorAll('.cc__dot'), at: 0, onScreen: false };
      root._cc = card;
      cards.push(card);
      io.observe(root);
    });
  }

  /* ---------- boot ---------- */
  function boot() {
    initReveal();
    initProgress();
    initParallax();
    initCountUp();
    initNewsletter();
    initColourCycle();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
